<template>
  <filter-and-sort-box
    class="generic-accordion"
    :data="data"
    :headers="headers"
    :columnsToSort="columnsToSort"
    :columnsToNotSort="columnsToNotSort"
    :useSearch="useSearch"
    @searchQuery="searchQuery"
  >
    <template v-slot:rows="{ rows }">
      <tr
        v-for="(d, index) in getRows(rows)"
        :class="{
          child: d.isChild,
          'last-child': d.isLastChild,
          parent: !d.isChild,
          paddingRow: Object.keys(d).length === 0,
          expanded: isRowExpanded(d),
        }"
        :key="'data' + index"
        @click="expand(d)"
      >
        <slot name="row" :row="d">
          <td
            v-for="(key, index1) in Object.keys(headers)"
            :key="key + index1"
            :style="{
              textAlign:
                types && types[key] && types[key].textAlign
                  ? types[key].textAlign
                  : 'left',
            }"
          >
            <span v-if="d[key] === undefined"></span>
            <span
              v-else-if="
                !types[key] || types[key].type.toLowerCase() === 'string'
              "
            >
              &#10240;<i
                v-if="index1 === 0 && !d.isChild"
                class="arrow"
                :class="isRowExpanded(d) ? 'down' : 'right'"
              ></i>
              <span v-else>&#10240;</span>
              &#10240;{{ d[key] }}
            </span>
            <grafokett-button
              v-else-if="types[key].type.toLowerCase() === 'button'"
              v-on:click.stop
              @click="$emit('buttonClicked', { button: key, data: d })"
              :class="types[key].classes ? types[key].classes : ''"
            >
              {{ d[key] }}
            </grafokett-button>
            <grafokett-input
              v-else-if="types[key].type.toLowerCase() === 'input'"
              :class="types[key].classes ? types[key].classes : ''"
              v-model="d[key]"
              :disabled="d.disabled"
              :inline-save="inputInlineSave"
              @inlineSave="$emit('inputInlineSave', d)"
            />
            <div
              v-else-if="types[key].type.toLowerCase() === 'number-input'"
              class="w-full"
            >
              <number-input
                v-model="d[key]"
                @input="$emit('inputInlineSage', d)"
                :disabled="d.disabled"
              >
              </number-input>
            </div>
          </td>
        </slot>
      </tr>
    </template>
    <div v-if="renderComponent"></div>
  </filter-and-sort-box>
</template>

<script>
import FilterAndSortBox from "components/FilterAndSortBox.vue";
import GrafokettInput from "components/forms/Input.vue";
import GrafokettButton from "components/forms/Button.vue";
import NumberInput from "./forms/NumberInput.vue";

export default {
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Object,
      default: () => {},
    },
    columnsToSort: {
      type: Array,
      default: () => [],
    },
    columnsToNotSort: {
      type: Array,
      default: () => [],
    },
    types: {
      type: Object,
      default: () => {
        return {};
      },
    },
    inputInlineSave: {
      type: Boolean,
      default: false,
    },
    useSearch: {
      type: Boolean,
      default: true,
    },
    idKey: {
      type: String,
      default: "id",
    },
  },
  components: {
    FilterAndSortBox,
    GrafokettInput,
    GrafokettButton,
    NumberInput,
  },
  data() {
    return {
      expanded: {},
      renderComponent: false,
    };
  },
  methods: {
    isRowExpanded(row) {
      if (row.isChild || Object.keys(row).length === 0) return;
      const id = "data" + row[this.idKey];
      return this.expanded[id];
    },
    expand(row) {
      if (row.isChild || Object.keys(row).length === 0) return;
      const id = "data" + row[this.idKey];
      this.expanded[id] = !this.expanded[id];
      this.forceRerender();
    },
    searchQuery(value) {
      this.$emit("searchQuery", value);
    },
    getRows(rows) {
      const scope = this;
      const r = [];
      rows
        .filter(row => !row.isChild)
        .forEach(row => {
          const id = "data" + row[this.idKey];
          r.push(row);
          if (scope.expanded[id] === undefined) scope.expanded[id] = false;
          if (scope.expanded[id] && row.children) {
            row.children.forEach((child, i) => {
              child.isChild = true;
              r.push(child);
              if (row.children.length - 1 === i)
                r[r.length - 1].isLastChild = true;
            });
          }
          r.push({});
        });
      return r;
    },
    forceRerender() {
      // Remove my-component from the DOM
      this.renderComponent = true;

      this.$nextTick(() => {
        // Add the component back in
        this.renderComponent = false;
      });
    },
  },
};
</script>
<style lang="scss">
.generic-accordion {
  border-radius: 6px;
  color: theme("colors.textcolor");

  .table .filter-table {
    > tbody {
      background-color: transparent !important;

      > tr {
        background-color: theme("colors.primary-light");

        &.paddingRow {
          color: transparent;
          background-color: transparent;
          border: none;

          * {
            display: none;
          }

          > td {
            display: table-cell;
            color: transparent;
            background-color: transparent !important;
            border: none;
            padding: 0.25rem;
          }
        }

        $borde-size: 0.2rem;
        &.parent:not(.paddingRow) {
          &.expanded {
            > td:nth-child(1) {
              border-left: 1px solid theme("colors.textcolor");
              border-radius: $borde-size 0 0 0;
            }
            > td {
              border-top: 1px solid theme("colors.textcolor");
              border-bottom: 1px solid theme("colors.textcolor");
            }
            > td:last-child {
              border-right: 1px solid theme("colors.textcolor");
              border-radius: 0 $borde-size 0 0;
            }
          }
          &:not(.expanded) {
            > td:nth-child(1) {
              border-radius: $borde-size 0 0 $borde-size;
            }
            > td {
              border: none;
            }
            > td:last-child {
              border-radius: 0 $borde-size $borde-size 0;
            }
          }
        }

        &.child {
          > td:nth-child(1) {
            border-left: 1px solid theme("colors.textcolor");
          }
          > td:last-child {
            border-right: 1px solid theme("colors.textcolor");
          }
        }
        &.last-child {
          > td:nth-child(1) {
            border-radius: 0 0 0 $borde-size;
          }
          > td {
            border-bottom: 1px solid theme("colors.textcolor") !important;
          }
          > td:last-child {
            border-radius: 0 0 $borde-size 0;
          }
        }
      }
    }

    > thead > tr > th,
    > tbody > tr > td {
      padding: 1rem;
    }
  }
}
</style>
